/**
 * Pantalla de "activá la verificación en dos pasos de Google" (Vault F2).
 *
 * Dos usos: (a) bloqueo de todo Internal pasada la fecha de enforcement
 * (DashboardLayout), (b) gate del Vault, que la exige siempre
 * (VaultProtectedRoute, F3). Nunca una página vacía: explica qué hacer, abre
 * la configuración de Google y permite re-verificar sin recargar.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, LogOut, RefreshCw, Settings2, ShieldAlert } from 'lucide-react';
import { signOut as amplifySignOut } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { rememberReturnTo } from '@/lib/return-to';

export const GOOGLE_2SV_URL = 'https://myaccount.google.com/signinoptions/two-step-verification';

interface MfaRequiredScreenProps {
  variant: 'internal' | 'vault';
  /** true/false según Google; null = no se pudo verificar */
  enrolled: boolean | null;
  onRecheck: () => Promise<unknown> | void;
  rechecking?: boolean;
  /** Admin de Internal: link a la pestaña Seguridad para correr/apagar el enforcement. */
  adminEscapeHref?: string;
}

export function MfaRequiredScreen({ variant, enrolled, onRecheck, rechecking, adminEscapeHref }: MfaRequiredScreenProps) {
  const { t } = useTranslation();
  const [checkedOnce, setCheckedOnce] = useState(false);

  const recheck = async () => {
    setCheckedOnce(true);
    await onRecheck();
  };

  const logout = async () => {
    // al volver a entrar (ya con 2SV) se vuelve a esta misma pantalla
    rememberReturnTo(window.location);
    try {
      await amplifySignOut();
    } finally {
      window.location.href = '/login';
    }
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <Card className="w-full max-w-lg p-8">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold tracking-tight">
              {t('auth.mfa.required_title', 'Activá la verificación en dos pasos de Google')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {variant === 'vault'
                ? t('auth.mfa.required_vault_body', 'Para abrir el Vault necesitás la verificación en dos pasos activada en tu cuenta @winclap.com. Se configura una sola vez.')
                : t('auth.mfa.required_body', 'Internal ya exige el segundo factor de Google para entrar. Es la misma cuenta @winclap.com con la que iniciás sesión; se configura una sola vez.')}
            </p>
          </div>
        </div>

        <ol className="mt-6 space-y-2 text-sm">
          {[
            t('auth.mfa.step_1', 'Abrí la configuración de seguridad de tu cuenta de Google.'),
            t('auth.mfa.step_2', 'Activá la verificación en dos pasos (Google Authenticator, llave física o notificación en el teléfono).'),
            t('auth.mfa.step_3', 'Volvé acá y tocá "Ya la activé". Google puede tardar un minuto en reflejarlo.'),
          ].map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold">{i + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>

        {checkedOnce && enrolled === false && (
          <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            {t('auth.mfa.still_missing', 'Google todavía no muestra la verificación activada. Probá de nuevo en un minuto.')}
          </p>
        )}
        {enrolled === null && (
          <p className="mt-4 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
            {t('auth.mfa.unverified', 'No pudimos verificar el estado de tu cuenta ahora mismo.')}
          </p>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          <Button asChild>
            <a href={GOOGLE_2SV_URL} target="_blank" rel="noreferrer" data-tour="mfa-activate">
              <ExternalLink className="mr-2 h-4 w-4" />
              {t('auth.mfa.activate', 'Activar en Google')}
            </a>
          </Button>
          <Button variant="outline" onClick={recheck} disabled={!!rechecking} data-tour="mfa-recheck">
            <RefreshCw className={`mr-2 h-4 w-4 ${rechecking ? 'animate-spin' : ''}`} />
            {rechecking ? t('auth.mfa.rechecking', 'Verificando…') : t('auth.mfa.recheck', 'Ya la activé')}
          </Button>
          {variant === 'internal' && (
            <Button variant="ghost" onClick={logout} className="ml-auto">
              <LogOut className="mr-2 h-4 w-4" />
              {t('auth.mfa.logout', 'Cerrar sesión')}
            </Button>
          )}
        </div>
        {adminEscapeHref && (
          <p className="mt-4 border-t pt-3 text-xs text-muted-foreground">
            <Link to={adminEscapeHref} className="inline-flex items-center gap-1 hover:text-foreground">
              <Settings2 className="h-3 w-3" />
              {t('auth.mfa.admin_escape', 'Soy admin: ir a Seguridad para ajustar la fecha de enforcement')}
            </Link>
          </p>
        )}
      </Card>
    </div>
  );
}
